import React, { useEffect, useRef, useState } from 'react';
import { FEATURES, tableSize } from './seatingUtils';

const ROOM_RATIO = 0.625;

// Pointer drag for anything on the room. Calls onMove live and onDone once on release.
const startDrag = (getStart, onMove, onDone, onClick, roomRef) => (e) => {
  if (e.button !== 0) return;
  e.stopPropagation();
  const rect = roomRef.current.getBoundingClientRect();
  const start = getStart();
  const sx = e.clientX; const sy = e.clientY;
  let moved = false; let last = start;
  const move = (ev) => {
    const dx = ((ev.clientX - sx) / rect.width) * 100;
    const dy = ((ev.clientY - sy) / rect.height) * 100;
    if (!moved && Math.abs(ev.clientX - sx) + Math.abs(ev.clientY - sy) < 4) return;
    moved = true;
    last = onMove(start, dx, dy);
  };
  const up = () => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    if (moved) onDone(last); else onClick?.();
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
};

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

const TableShape = ({ t, names, selected, highlight, unit, onPointerDown, onDropGuest }) => {
  const [over, setOver] = useState(false);
  const { w, h } = tableSize(t);
  const full = names.length >= Number(t.seats);
  const font = clamp(unit * 1.25, 10, 14);
  const maxLines = Math.max(2, Math.floor((h * unit * 0.62) / (font * 1.2)) - 1);
  const twoCol = t.shape === 'rect' && Number(t.rotation) !== 90 && names.length > maxLines;
  const shown = names.slice(0, twoCol ? maxLines * 2 : maxLines);
  const radius = t.shape === 'round' ? '9999px' : t.shape === 'sweetheart' ? `${unit * 3}px ${unit * 3}px ${unit}px ${unit}px` : `${unit * 0.8}px`;
  return (
    <div
      data-testid={`reception-table-${t.id}`}
      onPointerDown={onPointerDown}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const gid = e.dataTransfer.getData('text/guest-id'); if (gid) onDropGuest(gid); }}
      style={{ left: `${t.x}%`, top: `${t.y}%`, width: w * unit, height: h * unit, borderRadius: radius, touchAction: 'none' }}
      className={`absolute -translate-x-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing select-none border-2 flex flex-col items-center justify-center text-center px-1 overflow-hidden transition-[box-shadow,border-color] duration-200
        ${t.shape === 'sweetheart' ? 'bg-[#FFF1F3]' : 'bg-[#FFFDF9]'}
        ${selected ? 'border-[#7B2C3B] shadow-[0_8px_24px_rgba(123,44,59,0.25)] z-20' : full ? 'border-[#2E7D32] z-10' : 'border-[#B8896C] z-10'}
        ${over ? 'ring-4 ring-[#7B2C3B]/40' : ''} ${highlight ? 'ring-4 ring-[#F59E0B] animate-pulse' : ''}`}
    >
      <p className="font-serif leading-none text-[#7B2C3B]" style={{ fontSize: font + 1 }}>{t.label}</p>
      <p className={`leading-tight mb-0.5 font-medium ${full ? 'text-[#2E7D32]' : 'text-[#6A6A6A]'}`} style={{ fontSize: font - 2 }}>{names.length}/{t.seats}</p>
      <div className={twoCol ? 'grid grid-cols-2 gap-x-2' : ''} style={{ fontSize: font - 1, lineHeight: 1.15 }}>
        {shown.map((n, i) => <p key={i} className="truncate text-[#2A2A2A]" style={{ maxWidth: twoCol ? (w * unit) / 2 - 6 : w * unit * 0.8 }}>{n}</p>)}
      </div>
      {names.length > shown.length && <p className="text-[#9A8A7E]" style={{ fontSize: font - 2 }}>+{names.length - shown.length} more</p>}
    </div>
  );
};

const Feature = ({ f, selected, onPointerDown, onResize }) => {
  const meta = FEATURES[f.type];
  return (
    <div
      data-testid={`room-feature-${f.id}`}
      onPointerDown={onPointerDown}
      style={{ left: `${f.x}%`, top: `${f.y}%`, width: `${f.w}%`, height: `${f.h}%`, background: meta.color, borderColor: selected ? '#7B2C3B' : meta.border, touchAction: 'none' }}
      className={`absolute -translate-x-1/2 -translate-y-1/2 select-none cursor-grab active:cursor-grabbing rounded-xl ${meta.area ? 'border-2 border-dashed' : 'border-2'} ${selected ? 'shadow-lg z-30' : meta.area ? 'z-0' : 'z-[5]'}`}
    >
      <span className={`absolute ${meta.area ? 'top-1.5 left-2.5' : 'inset-0 flex items-center justify-center text-center'} text-[11px] font-medium uppercase tracking-wider`} style={{ color: meta.ink }}>{f.label || meta.label}</span>
      {selected && <span data-testid={`room-feature-resize-${f.id}`} onPointerDown={onResize} className="absolute -right-1.5 -bottom-1.5 w-4 h-4 rounded-full bg-[#7B2C3B] border-2 border-white cursor-nwse-resize" />}
    </div>
  );
};

export const FloorPlan = ({ tables, features, room, namesFor, selected, highlight, zoom, onSelect, onMoveTable, onChangeFeature, onCommitFeatures, onDropGuest }) => {
  const roomRef = useRef(null);
  const [width, setWidth] = useState(900);
  const [local, setLocal] = useState({});
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    if (roomRef.current) ro.observe(roomRef.current);
    return () => ro.disconnect();
  }, []);
  const unit = width / 100;
  const pos = (t) => ({ ...t, ...(local[t.id] || {}) });

  const dragTable = (t) => startDrag(
    () => ({ x: Number(t.x), y: Number(t.y) }),
    (s, dx, dy) => { const p = { x: clamp(s.x + dx, 2, 98), y: clamp(s.y + dy, 2, 98) }; setLocal((l) => ({ ...l, [t.id]: p })); return p; },
    (p) => { onMoveTable(t.id, p); setLocal((l) => { const n = { ...l }; delete n[t.id]; return n; }); },
    () => onSelect({ kind: 'table', id: t.id }), roomRef,
  );
  const dragFeature = (f, resize) => startDrag(
    () => ({ ...f }),
    (s, dx, dy) => {
      const p = resize ? { ...s, w: clamp(s.w + dx * 2, 3, 100), h: clamp(s.h + dy * 2, 3, 100) } : { ...s, x: clamp(s.x + dx, 0, 100), y: clamp(s.y + dy, 0, 100) };
      onChangeFeature(p); return p;
    },
    () => onCommitFeatures(),
    () => onSelect({ kind: 'feature', id: f.id }), roomRef,
  );

  return (
    <div className="overflow-auto rounded-2xl border border-[#EFE3D8] bg-[#EFE6DC]" style={{ maxHeight: '75vh' }}>
      <div
        ref={roomRef} data-testid="reception-canvas-positioned" onPointerDown={() => onSelect(null)}
        className="relative mx-auto bg-[#FBF7F2]"
        style={{ width: `${zoom * 100}%`, minWidth: 560 * zoom, aspectRatio: `1 / ${ROOM_RATIO}`, backgroundImage: 'radial-gradient(#E4D6C8 1px, transparent 1px)', backgroundSize: `${unit * 2.5}px ${unit * 2.5}px` }}
      >
        {room.floorplan_url && <img src={room.floorplan_url} alt="Venue floor plan" draggable={false} className="absolute inset-0 w-full h-full object-contain pointer-events-none" style={{ opacity: room.floorplan_opacity ?? 0.5 }} data-testid="floorplan-image" />}
        {features.map((f) => (
          <Feature key={f.id} f={f} selected={selected?.kind === 'feature' && selected.id === f.id}
            onPointerDown={dragFeature(f, false)} onResize={dragFeature(f, true)} />
        ))}
        {tables.map((t) => {
          const p = pos(t);
          return (
            <TableShape key={t.id} t={p} names={namesFor(t)} unit={unit}
              selected={selected?.kind === 'table' && selected.id === t.id} highlight={highlight === t.id}
              onPointerDown={dragTable(p)} onDropGuest={(gid) => onDropGuest(gid, t)} />
          );
        })}
      </div>
    </div>
  );
};

export default FloorPlan;
